import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Users, Baby } from "lucide-react";
import { IMG } from "@/data/siteData";

const stats = [
  { icon: MapPin, value: "4", label: "lokalizacje w Poznaniu" },
  { icon: Users, value: "max 15", label: "dzieci w grupie" },
  { icon: Baby, value: "od 4 lat", label: "przyjmujemy zawodników" },
];

const LINES = [
  { text: "Szkółka Młodego", accent: false },
  { text: "Piłkarza", accent: true },
  { text: "Wichniarek", accent: false },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} id="start" data-testid="hero-section" className="noise-blue text-[#F8FAFC] slant-bottom relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-28 lg:pt-24 lg:pb-36 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-4 mb-8">
            <motion.img
              src="/logo.png"
              alt="Herb SMP Wichniarek"
              initial={{ scale: 0, rotate: -16 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.1 }}
              className="h-20 w-20 lg:h-24 lg:w-24 object-contain drop-shadow-xl"
            />
            <motion.span
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.35, ease: EASE }}
              className="font-accent uppercase tracking-[0.3em] text-[#FACC15] text-xs sm:text-sm font-bold"
            >
              Poznań • Piłka nożna dla dzieci
            </motion.span>
          </div>

          <h1 className="font-display font-black uppercase tracking-tighter leading-none text-4xl sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.3 + i * 0.14, ease: EASE }}
                  className={`block ${line.accent ? "text-[#FACC15]" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
          >
            <p className="mt-6 text-blue-100 text-base md:text-lg max-w-xl font-medium">
              Uczymy piłki nożnej przez zabawę. Małe grupy, profesjonalna kadra
              i szkolenie oparte na wzorcach niemieckich szkółek — od przedszkolaka
              po młodego zawodnika.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#kontakt"
                data-testid="hero-cta-signup"
                className="bg-[#FACC15] text-[#0B1120] font-display font-black uppercase tracking-tight px-8 py-4 border-2 border-[#FACC15] hover:bg-transparent hover:text-[#FACC15] transition-colors duration-200"
              >
                Trening próbny za darmo
              </a>
              <a
                href="#o-nas"
                data-testid="hero-cta-about"
                className="border-2 border-white/60 text-[#F8FAFC] font-display font-black uppercase tracking-tight px-8 py-4 hover:border-[#FACC15] hover:text-[#FACC15] transition-colors duration-200"
              >
                Poznaj nas
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05, ease: EASE }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-xl"
          >
            {stats.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.replace(/\s/g, '-')}`}>
                <s.icon className="text-[#FACC15] mb-2" size={22} />
                <div className="font-display font-black text-2xl lg:text-3xl uppercase tracking-tight">{s.value}</div>
                <div className="font-accent text-[11px] uppercase tracking-widest text-blue-200 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
          className="lg:col-span-5 relative"
        >
          <div className="border-4 border-[#FACC15] translate-x-3 translate-y-3 absolute inset-0" aria-hidden="true" />
          <div className="relative w-full h-[420px] lg:h-[520px] overflow-hidden">
            <motion.img
              src={IMG.hero}
              alt="Młodzi piłkarze SMP Wichniarek w biegu"
              data-testid="hero-image"
              style={{ y: imgY }}
              className="w-full h-[calc(100%+160px)] -mt-20 object-cover"
            />
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-[#FACC15] py-3 overflow-hidden -rotate-1 origin-left scale-105">
        <div className="animate-marquee whitespace-nowrap flex font-display font-black uppercase tracking-tight text-[#1E3A8A] text-lg">
          {[0, 1].map((n) => (
            <span key={n} className="flex">
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className="mx-6">
                  Szkółka Młodego Piłkarza Wichniarek <span className="text-white mx-4">★</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
